import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Plus } from 'lucide-react';
import { format } from 'date-fns';
import imageCompression from 'browser-image-compression';
import { TimelineEntry } from '../../types';
import { supabase } from '../../utils/supabaseClient';
import { useAppContext } from '../../context/AppContext';

interface TimelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (entry: Omit<TimelineEntry, 'id' | 'meetingCount'>) => Promise<void>;
  editingEntry?: TimelineEntry | null;
  defaultDate?: string;
}

const TimelineModal: React.FC<TimelineModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingEntry,
  defaultDate
}) => {
  const { coupleId, user } = useAppContext();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newDate, setNewDate] = useState(defaultDate || format(new Date(), 'yyyy-MM-dd'));
  const [newTags, setNewTags] = useState('');
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (editingEntry) {
      setNewTitle(editingEntry.title);
      setNewContent(editingEntry.content);
      setNewLocation(editingEntry.location || '');
      setNewDate(editingEntry.event_date);
      setNewTags(editingEntry.tags.join(', '));
      setImagePreviews(editingEntry.image_urls || []);
      setSelectedFiles([]);
    } else {
      setNewTitle('');
      setNewContent('');
      setNewLocation('');
      setNewDate(defaultDate || format(new Date(), 'yyyy-MM-dd'));
      setNewTags('');
      setImagePreviews([]);
      setSelectedFiles([]);
    }
    setUploadProgress(0);
    setUploadStatus('');
    setIsUploading(false);
  }, [editingEntry, defaultDate, isOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files]);
      
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    const existingCount = imagePreviews.filter(p => p.startsWith('http')).length;
    if (index >= existingCount) {
      setSelectedFiles(prev => prev.filter((_, i) => i !== (index - existingCount)));
    }
  };

  const handleSave = async () => {
    if (!newTitle.trim() || !user?.id) return;

    setIsUploading(true);
    setUploadProgress(0);

    let finalImageUrls: string[] = imagePreviews.filter(p => p.startsWith('http'));

    try {
      // 1. Image Compression
      let filesToUpload = selectedFiles;
      if (selectedFiles.length > 0) {
        setUploadStatus('이미지 압축 중...');
        try {
          const compressionOptions = {
            maxSizeMB: 1,
            maxWidthOrHeight: 1920,
            useWebWorker: true
          };

          filesToUpload = await Promise.all(
            selectedFiles.map(async (file) => {
              try {
                return await imageCompression(file, compressionOptions);
              } catch (err) {
                console.error('[Compression Error] Failed for file:', file.name, err);
                throw new Error(`이미지 압축에 실패했습니다 (${file.name})`);
              }
            })
          );
          console.log('[Compression] Success. Files:', filesToUpload.length);
        } catch (compressionErr: any) {
          alert(compressionErr.message || '이미지 압축 과정에서 에러가 발생했습니다.');
          setIsUploading(false);
          return;
        }
      }

      // 2. Separate Image Upload Logic
      const storageFolder = coupleId || user.id;
      const uploadedUrls: string[] = [];

      setUploadStatus('이미지 업로드 중...');
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const fileExt = file.name.split('.').pop() || 'jpg';
        const fileName = `${Date.now()}_${i}.${fileExt}`;
        const filePath = `${storageFolder}/${fileName}`;

        console.log(`[Storage] Uploading to IMAGE/${filePath}`);
        const { error: uploadError } = await supabase.storage
          .from('IMAGE')
          .upload(filePath, file);

        if (uploadError) {
          console.error('[Storage Error] Upload failed:', uploadError);
          continue; 
        }

        const { data: { publicUrl } } = supabase.storage
          .from('IMAGE')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        setUploadProgress(Math.round(((i + 1) / filesToUpload.length) * 100));
      }
      
      finalImageUrls = [...finalImageUrls, ...uploadedUrls];
    } catch (err) {
      console.error('[Process Error] Critical failure:', err);
    }

    // 3. Prepare DB Data
    const tagArray = newTags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag !== '')
      .map(tag => tag.startsWith('#') ? tag : `#${tag}`);

    const entryData = {
      event_date: newDate,
      title: newTitle,
      content: newContent,
      location: newLocation,
      image_urls: finalImageUrls,
      tags: tagArray,
    };

    console.log('[Timeline] Final Data to DB:', entryData);

    // 4. Save to DB
    try {
      setUploadStatus('기록 저장 중...');
      await onSave(entryData);
      onClose();
    } catch (error: any) {
      console.error('[DB Error] Save failed:', error);
      alert(`저장에 실패했습니다.\n사유: ${error.message || '알 수 없는 서버 에러'}`);
    } finally {
      setIsUploading(false);
      setUploadStatus('');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div 
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="relative w-full max-lg bg-white rounded-t-[40px] sm:rounded-[40px] p-8 shadow-2xl max-h-[92vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-black text-charcoal tracking-tight">
                {editingEntry ? '추억 수정하기' : '추억 기록하기'}
              </h2>
              <button onClick={onClose} className="p-2 bg-secondary/50 rounded-full text-gray-400 hover:text-charcoal transition-colors">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-6">
              {/* Image Upload Area */}
              <div className="space-y-3">
                <label className="block text-[10px] font-black text-gray-400 ml-1 uppercase tracking-wider">사진 (여러 장 선택 가능)</label>
                <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-24 h-24 shrink-0 bg-secondary rounded-2xl flex flex-col items-center justify-center border-2 border-dashed border-gray-200 hover:border-primary/50 transition-colors"
                  >
                    <Plus className="text-gray-400" size={24} />
                    <span className="text-[10px] font-bold text-gray-400 mt-1">추가</span>
                  </button>
                  
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="w-24 h-24 shrink-0 rounded-2xl overflow-hidden relative group">
                      <img src={preview} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                      <button 
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-black/50 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                <input 
                  type="file" 
                  accept="image/*" 
                  multiple
                  ref={fileInputRef} 
                  onChange={handleImageChange} 
                  className="hidden" 
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">날짜</label>
                  <input 
                    type="date" 
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    className="w-full bg-secondary border-none rounded-2xl px-4 py-3.5 text-xs focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">장소</label>
                  <input 
                    type="text" 
                    placeholder="어디였나요?"
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    className="w-full bg-secondary border-none rounded-2xl px-4 py-3.5 text-xs focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">제목</label>
                <input 
                  type="text" 
                  placeholder="기록의 제목을 지어주세요"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full bg-secondary border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">해시태그 (쉼표로 구분)</label>
                <input 
                  type="text" 
                  placeholder="맛집, 여행, 드라이브"
                  value={newTags}
                  onChange={(e) => setNewTags(e.target.value)}
                  className="w-full bg-secondary border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-bold"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">이야기</label>
                <textarea 
                  rows={4}
                  placeholder="오늘 어떤 특별한 일이 있었나요?"
                  value={newContent}
                  onChange={(e) => setNewContent(e.target.value)}
                  className="w-full bg-secondary border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-primary outline-none transition-all font-medium resize-none"
                />
              </div>

              <button 
                onClick={handleSave}
                disabled={!newTitle.trim() || isUploading}
                className="w-full bg-primary text-white font-black py-5 rounded-2xl shadow-xl shadow-primary/30 active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100 text-sm tracking-tight flex items-center justify-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    <span>{uploadStatus} {uploadProgress > 0 ? `(${uploadProgress}%)` : ''}</span>
                  </>
                ) : (
                  <span>{editingEntry ? '변경사항 저장하기' : '기록 저장하기'}</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default TimelineModal;
